/**
 * Пробный Pro для Free (#2816, спека docs/plans/2026-10-26-free-pro-trial.md).
 *
 * Состояние живёт в `orgs.settings_json.proTrial = { startedAt, endsAt }` и
 * пишется один раз на организацию (`mergeSettingsIfUnchanged`): ключ уже есть —
 * второй старт ничего не меняет, проба не повторяется.
 *
 * Проба меняет ТОЛЬКО тариф, по которому решают замки функций (`gateTier`).
 * Минуты, цена минуты и лимиты (`getOrgTier`, `TIER_LIMITS`) идут по настоящему
 * тарифу — решение владельца 26.10.
 */

/** Срок пробы. `endsAt` хранится, а не вычисляется: смена срока не трогает начатые пробы. */
export const PRO_TRIAL_DAYS = 14;

const DAY_MS = 24 * 60 * 60 * 1000;

export type ProTrialState =
  | { state: 'none' }
  | { state: 'active'; startedAt: string; endsAt: string }
  /** `endedAt` = null — ключ нечитаем: замок закрыт, а не открыт. */
  | { state: 'ended'; endedAt: string | null };

function isValidIso(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length > 0 &&
    Number.isFinite(new Date(value).getTime())
  );
}

/**
 * Читает пробу из settings_json.
 *
 * Сбой чтения закрывает замок: нечитаемый ключ или битый JSON дают `ended`, а
 * не `none`. `none` разрешил бы конвейеру начать пробу поверх настроек, которые
 * никто не смог разобрать.
 */
export function readProTrial(
  settingsJson: string | null | undefined,
  now: Date
): ProTrialState {
  if (!settingsJson) return { state: 'none' };

  let parsed: unknown;
  try {
    parsed = JSON.parse(settingsJson);
  } catch {
    return { state: 'ended', endedAt: null };
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { state: 'ended', endedAt: null };
  }

  const trial = (parsed as Record<string, unknown>).proTrial;
  if (trial === undefined) return { state: 'none' };
  if (!trial || typeof trial !== 'object' || Array.isArray(trial)) {
    return { state: 'ended', endedAt: null };
  }

  const { startedAt, endsAt } = trial as Record<string, unknown>;
  if (!isValidIso(startedAt) || !isValidIso(endsAt))
    return { state: 'ended', endedAt: null };
  if (new Date(endsAt).getTime() <= new Date(startedAt).getTime()) {
    return { state: 'ended', endedAt: null };
  }

  return now.getTime() < new Date(endsAt).getTime()
    ? { state: 'active', startedAt, endsAt }
    : { state: 'ended', endedAt: endsAt };
}

/**
 * Тариф для замков функций: `'pro'`, если настоящий тариф Free и проба идёт;
 * иначе настоящий тариф. Подставляется только там, где тариф решает замок, —
 * не в биллинге и не в лимитах. Неизвестный тариф (`null`/`undefined`)
 * возвращается как есть и пробой не поднимается: что он значит для замка,
 * решает вызывающий, одинаково с пробой и без неё.
 */
export function gateTier<T extends string | null | undefined>(
  tier: T,
  settingsJson: string | null | undefined,
  now: Date
): T | 'pro' {
  if (tier !== 'free') return tier;
  return isProTrialActive(settingsJson, now) ? 'pro' : tier;
}

/** Проба Pro идёт (по настройкам организации; тариф не проверяет — это `gateTier`). */
export function isProTrialActive(
  settingsJson: string | null | undefined,
  now: Date
): boolean {
  return readProTrial(settingsJson, now).state === 'active';
}

/**
 * Что экран говорит о пробе (спека, «Показ пробы»): `available` — до старта
 * («14 дней функций Pro начнутся со следующего анализа»), `active` — баннер с
 * остатком дней, `ended` — строка о конце на замке (`endsAt` = null — ключ
 * нечитаем, дата неизвестна).
 */
export type ProTrialView =
  | { state: 'available'; endsAt: null }
  | { state: 'active'; endsAt: string }
  | { state: 'ended'; endsAt: string | null };

/**
 * Проба для показа — только у организации с настоящим тарифом Free: платная
 * свою пробу уже не видит. Обещание «начнётся со следующего анализа» — только при
 * включённом флаге `enableProTrial`, иначе проба не начнётся. Начатая и
 * закончившаяся проба показываются и при выключенном флаге: выключение
 * перестаёт начинать новые, но не обрывает начатые.
 */
export function proTrialView(
  realTier: string | null | undefined,
  settingsJson: string | null | undefined,
  trialFlagOn: boolean,
  now: Date
): ProTrialView | null {
  if (realTier !== 'free') return null;
  const trial = readProTrial(settingsJson, now);
  if (trial.state === 'active') return { state: 'active', endsAt: trial.endsAt };
  if (trial.state === 'ended') {
    // A trial key that cannot be read still means the trial was started; a
    // settings blob that cannot be read says nothing of a trial — the lock
    // stays (`gateTier`), with no line claiming a trial ended.
    if (trial.endedAt === null && !hasTrialKey(settingsJson)) return null;
    return { state: 'ended', endsAt: trial.endedAt };
  }
  return trialFlagOn ? { state: 'available', endsAt: null } : null;
}

function hasTrialKey(settingsJson: string | null | undefined): boolean {
  if (!settingsJson) return false;
  try {
    const parsed: unknown = JSON.parse(settingsJson);
    return (
      !!parsed &&
      typeof parsed === 'object' &&
      !Array.isArray(parsed) &&
      'proTrial' in parsed
    );
  } catch {
    return false;
  }
}

/** Значение ключа `proTrial` для старта в момент `startedAt`. */
export function buildProTrial(startedAt: Date): { startedAt: string; endsAt: string } {
  return {
    startedAt: startedAt.toISOString(),
    endsAt: new Date(startedAt.getTime() + PRO_TRIAL_DAYS * DAY_MS).toISOString(),
  };
}
